import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogIn, LogOut, Car, User, Radio, X, Zap } from "lucide-react";
import { toast } from "sonner";
import clienteApi from "@/lib/clienteApi";
import ObservacoesCpfPanel from "@/components/ObservacoesCpfPanel";

const LS_LAST_COLETOR = "brave_ultimo_coletor_manual";

/**
 * Lê id da portaria atualmente selecionada no PortariaSwitcher.
 * Retorna string ou null.
 */
function _portariaIdAtual() {
  try {
    const raw = sessionStorage.getItem("brave_cliente_portaria");
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.id ? String(p.id) : null;
  } catch {
    return null;
  }
}

/** Chave de memorização do último coletor — por portaria quando disponível,
 *  com fallback para a key global (retrocompatibilidade). */
function _lsKey() {
  const pid = _portariaIdAtual();
  return pid ? `${LS_LAST_COLETOR}:${pid}` : LS_LAST_COLETOR;
}

/**
 * Modal para registrar manualmente um acesso de usuário fixo (morador / funcionário)
 * na tabela acessos_XXXXXX, a partir da página de Busca.
 *
 * Props:
 *  - open, onOpenChange: controle do Dialog
 *  - morador: objeto do usuário fixo usado como referência (proprietário/base da EQL)
 *  - placaBuscada: string (opcional) — placa que originou a busca; usada como default
 *                  do campo placa quando o coletor selecionado for Veicular.
 *  - onRegistrado: callback (resposta do backend) após um registro bem-sucedido.
 */
export default function RegistrarAcessoFixoModal({ open, onOpenChange, morador, placaBuscada, onRegistrado }) {
  const [moradoresEQL, setMoradoresEQL] = useState([]);
  const [coletores, setColetores] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [condutorId, setCondutorId] = useState("");
  const [ncoletor, setNColetor] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null); // 1 | 2
  const [confirmPedestreOpen, setConfirmPedestreOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  // v3.13.1 (Fase A Parte 2) — Endpoints IoT vinculados à portaria atual
  // Cada entry vira um botão que dispara um popup ao Edge (HTTP local)
  // via HMAC; escuta `postMessage` para registrar latência/status.
  const [iotEndpoints, setIotEndpoints] = useState([]);
  const [iotFiring, setIotFiring] = useState(null); // endpoint_id em execução
  // v3.10.0 (Fase 2) — Texto opcional da observação-CPF digitado no painel.
  // Vai junto no payload do POST /cliente/acessos-manual. Se vazio, nenhuma
  // obs é criada. Zerado ao fechar/abrir o modal.
  const [observacaoTexto, setObservacaoTexto] = useState("");

  // Carrega moradores da EQL e lista de coletores ao abrir
  useEffect(() => {
    if (!open) {
      // v3.10.0 — limpa texto de obs entre aberturas do modal (evita
      // que um texto digitado numa consulta anterior "vaze" para outra)
      setObservacaoTexto("");
      return;
    }
    if (!morador) return;
    (async () => {
      setCarregando(true);
      try {
        // v3.13.1 — Busca endpoints IoT ATIVOS da portaria atual (para
        // renderizar botões de acionamento no modal). Falha silenciosa —
        // apenas oculta a seção se algo der errado.
        const pid = _portariaIdAtual();
        const iotUrl = pid
          ? `/cliente/iot/endpoints?portaria_id=${encodeURIComponent(pid)}`
          : "/cliente/iot/endpoints";
        const [resEql, resCol, resIot] = await Promise.all([
          clienteApi.get(`/cliente/moradores/por-eql?eql=${encodeURIComponent(morador.eql || "")}`),
          clienteApi.get("/cliente/coletores"),
          clienteApi.get(iotUrl).catch(() => ({ data: [] })),
        ]);
        setIotEndpoints(Array.isArray(resIot?.data) ? resIot.data : []);
        const lista = resEql.data || [];
        // v3.12.7 — Ordenação por prioridade: Moradores (tipo=16) primeiro,
        // Familiares (tipo=21) em seguida, e todos os demais (prestadores,
        // funcionários, terceirizados) por último. Dentro de cada grupo,
        // ordena por nome. Ajuda o porteiro a achar rápido quem "mora" ali.
        const _prio = (t) => {
          const n = Number(t || 0);
          if (n === 16) return 0;
          if (n === 21) return 1;
          return 2;
        };
        lista.sort((a, b) => {
          const pa = _prio(a.tipo_cartao);
          const pb = _prio(b.tipo_cartao);
          if (pa !== pb) return pa - pb;
          return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
        });
        // Garante que o morador pesquisado esteja no topo (se não vier na lista por qualquer motivo)
        const jaTem = lista.some((m) => m.id === morador.id);
        const finalList = jaTem ? lista : [morador, ...lista];
        setMoradoresEQL(finalList);
        setCondutorId(morador.id);

        const colList = (resCol.data || []).filter((c) => c.SeAtivo !== "N" && c.NColetor);
        setColetores(colList);

        // Restaurar último coletor usado (memorização POR PORTARIA quando há
        // portaria selecionada; senão fallback global).
        const key = _lsKey();
        let last = localStorage.getItem(key);
        // Fallback retrocompatível: se não houver registro POR PORTARIA, tenta
        // a key global antiga.
        if (!last && key !== LS_LAST_COLETOR) {
          last = localStorage.getItem(LS_LAST_COLETOR);
        }
        if (last && colList.some((c) => String(c.NColetor) === String(last))) {
          setNColetor(String(last));
        } else if (colList.length === 1) {
          setNColetor(String(colList[0].NColetor));
        }
      } catch (e) {
        toast.error("Erro ao carregar moradores/dispositivos");
      } finally {
        setCarregando(false);
      }
    })();
  }, [open, morador]);

  const condutor = moradoresEQL.find((m) => m.id === condutorId) || morador;
  const coletorSel = coletores.find((c) => String(c.NColetor) === ncoletor);
  const tipoAcessoColetor = coletorSel?.tipo_acesso ?? 0;
  const ehPedestre = Number(tipoAcessoColetor) === 1;

  const executarRegistro = async (status) => {
    if (!condutor) { toast.error("Selecione o condutor"); return; }
    if (!ncoletor) { toast.error("Selecione o dispositivo"); return; }
    setSalvando(true);
    try {
      const body = {
        matricula: Number(condutor.matricula),
        nome: condutor.nome,
        tipo_cartao: Number(condutor.tipo_cartao || 0),
        drt: String(condutor.matricula || ""),
        ncoletor: Number(ncoletor),
        placa: ehPedestre ? null : (placaBuscada || morador?.placa || null),
        status,
        sentido: status === 1 ? "E" : "S",
        eql: condutor.eql || null,
        cpf: condutor.cpf || null,
        // v3.10.0 — Se o operador digitou uma observação no painel, ela
        // vai junto e o backend cria a obs vinculada (origem=acesso_manual,
        // evento_tipo=entrada|saida, acesso_log_id=X).
        observacao_texto: (observacaoTexto || "").trim() || null,
      };
      const res = await clienteApi.post("/cliente/acessos-manual", body);
      // Memoriza último coletor usado — POR PORTARIA atual (com fallback global).
      try {
        localStorage.setItem(_lsKey(), String(ncoletor));
      } catch { /* storage cheio/bloqueado */ }
      toast.success(`Acesso registrado — ${status === 1 ? "Entrada" : "Saída"} (#${res.data.registro})`);
      if (onRegistrado) onRegistrado(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao registrar acesso");
    } finally {
      setSalvando(false);
    }
  };

  const tentarRegistrar = (status) => {
    if (!condutor || !ncoletor) {
      if (!condutor) toast.error("Selecione o condutor");
      else toast.error("Selecione o dispositivo");
      return;
    }
    // Regra de confirmação: coletor é Pedestre mas temos contexto de placa (veículo)
    const temPlaca = !!(placaBuscada || morador?.placa);
    if (ehPedestre && temPlaca) {
      setPendingStatus(status);
      setConfirmPedestreOpen(true);
      return;
    }
    executarRegistro(status);
  };

  const placaParaMostrar = placaBuscada || morador?.placa || "—";

  // v3.13.1/2/7 — Aciona endpoint IoT.
  // • strategy=cloud_enqueue (ESP32) → POST /fire (sync, sem popup)
  // • strategy=popup_edge (ZKTeco/genérico) → popup HTTP+HMAC ao BSS-Edge local
  // • v3.13.7 — Se o endpoint tem registra_sentido="E"/"S", após o acionamento
  //   OK dispara automaticamente executarRegistro(1|2) (entrada/saída).
  const dispararEndpoint = async (ep) => {
    if (!ep?.endpoint_id || !ep?.acionamento_id) return;
    // Se vai registrar automaticamente, exige condutor + coletor antes de acionar
    const autoStatus = ep.registra_sentido === "E" ? 1 : (ep.registra_sentido === "S" ? 2 : null);
    if (autoStatus !== null) {
      if (!condutor) { toast.error("Selecione o condutor antes de acionar (endpoint registra auto)"); return; }
      if (!ncoletor) { toast.error("Selecione o dispositivo antes de acionar (endpoint registra auto)"); return; }
    }
    setIotFiring(ep.endpoint_id);
    const t0 = Date.now();
    let acionamentoOk = false;
    try {
      const r = await clienteApi.post(
        `/cliente/iot/endpoints/${ep.acionamento_id}/build-url`,
        {
          placa: (placaBuscada || morador?.placa || "").toString().toUpperCase() || undefined,
          sentido: "M",  // M = manual (registrar acesso)
        },
      );
      const info = r.data || {};
      if (info.strategy === "cloud_enqueue") {
        const fr = await clienteApi.post(`/cliente/iot/endpoints/${ep.acionamento_id}/fire`);
        const latency_ms = Date.now() - t0;
        const nEnf = (fr.data?.enfileirados || []).length;
        toast.success(`${ep.endpoint_nome} — ${nEnf} comando(s) na Cloud em ${latency_ms}ms`);
        await clienteApi.post("/cliente/iot/command-log", {
          endpoint_id: ep.endpoint_id, acionamento_id: ep.acionamento_id,
          status: "ok", latency_ms,
          mensagem: `ESP32 enqueue ok (${nEnf} relés)`, origem: "acesso",
        });
        acionamentoOk = true;
        // v3.13.8 — NÃO usa return aqui, senão pula o registro automático
        // pós-acionamento (que fica DEPOIS do try/finally e fechava o modal
        // no ZKTeco mas não no ESP32).
      } else {
        // popup_edge → BSS-Edge local
      let resultReceived = false;
      let receivedData = null;
      const listener = (evt) => {
        if (!evt.data || evt.data.endpoint_id !== ep.endpoint_id) return;
        resultReceived = true;
        receivedData = evt.data;
      };
      window.addEventListener("message", listener);
      try {
        const { url } = info;
        const w = 380, h = 140;
        const left = (window.screen?.availWidth || 1920) - w - 10;
        const top = (window.screen?.availHeight || 1080) - h - 60;
        const popup = window.open(
          url, "brave_edge_cmd",
          `width=${w},height=${h},left=${left},top=${top},resizable=no,scrollbars=no,toolbar=no,menubar=no,location=no,status=no`,
        );
        if (!popup) {
          toast.error("Popup bloqueado — permita popups deste site nas configurações do navegador");
          await clienteApi.post("/cliente/iot/command-log", {
            endpoint_id: ep.endpoint_id, acionamento_id: ep.acionamento_id,
            status: "erro", mensagem: "popup bloqueado", origem: "acesso",
          });
          return;
        }
        const deadline = Date.now() + 3000;
        while (Date.now() < deadline && !resultReceived) {
          await new Promise((res) => setTimeout(res, 100));
        }
        const latency_ms = Date.now() - t0;
        if (resultReceived && receivedData?.status === "ok") {
          toast.success(`${ep.endpoint_nome} — OK em ${latency_ms}ms`);
          await clienteApi.post("/cliente/iot/command-log", {
            endpoint_id: ep.endpoint_id, acionamento_id: ep.acionamento_id,
            status: "ok", latency_ms, mensagem: receivedData?.mensagem, origem: "acesso",
          });
          acionamentoOk = true;
        } else {
          toast.warning(`${ep.endpoint_nome} — Timeout em ${latency_ms}ms`);
          await clienteApi.post("/cliente/iot/command-log", {
            endpoint_id: ep.endpoint_id, acionamento_id: ep.acionamento_id,
            status: resultReceived ? "erro" : "timeout", latency_ms,
            mensagem: receivedData?.mensagem || "sem postMessage do Edge", origem: "acesso",
          });
        }
      } finally {
        window.removeEventListener("message", listener);
      }
      }  // v3.13.8 — fecha o else { popup_edge }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Falha ao acionar ${ep.endpoint_nome}`);
    } finally {
      setIotFiring(null);
    }
    // v3.13.7 — Registro automático de acesso após acionamento OK.
    // Se registra_sentido é "E"/"S", chama executarRegistro(1|2). Isso
    // fecha o modal automaticamente (executarRegistro faz onOpenChange(false)).
    if (acionamentoOk && autoStatus !== null) {
      await executarRegistro(autoStatus);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="registrar-acesso-fixo-dialog" className="bg-brand-dark/95 border-brand-ice/20 text-brand-ice max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Radio className="w-5 h-5 text-brand-light" />
              Registrar Acesso — {morador?.nome}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Resumo do veículo/EQL (somente leitura) */}
            <div className="flex flex-wrap items-center gap-3 text-xs rounded-lg bg-white/5 border border-brand-ice/10 p-3">
              <span className="inline-flex items-center gap-1.5 text-amber-300 font-mono font-bold">
                <Car className="w-3.5 h-3.5" />
                {placaParaMostrar}
              </span>
              {morador?.modelo_veiculo && <span className="text-brand-ice/70">{morador.modelo_veiculo}</span>}
              {morador?.cor_veiculo && <span className="text-brand-ice/70">{morador.cor_veiculo}</span>}
              <span className="text-brand-ice/40">|</span>
              <span><span className="text-brand-ice/40">EQL:</span> <span className="font-mono text-brand-ice">{morador?.eql || "—"}</span></span>
            </div>

            {/* v3.10.0 — Painel Observações-CPF (Fase 2, mode="deferred").
                Texto digitado aqui vai junto no POST /cliente/acessos-manual
                quando o operador clica Entrada/Saída. Se vazio, nenhuma obs
                é criada. O contador no header reflete quantas obs esse CPF
                já tem (com destaque âmbar quando existem obs HOJE). */}
            {condutor?.cpf ? (
              <ObservacoesCpfPanel
                cpf={condutor.cpf}
                nomeSnapshot={condutor.nome || ""}
                origem="busca_avulsa"
                mode="deferred"
                onTextoChange={setObservacaoTexto}
                defaultOpen={false}
                testIdPrefix="registrar-acesso-obs"
                observacaoPrincipal={condutor.observacoes || ""}
              />
            ) : (
              <p className="text-[11px] text-brand-ice/40 italic">
                Observações desativadas — condutor sem CPF cadastrado.
              </p>
            )}

            {/* Dispositivo (primeiro) */}
            <div className="space-y-1">
              <Label className="text-xs text-brand-ice/70 flex items-center gap-1">
                <Radio className="w-3.5 h-3.5" />
                Dispositivo
              </Label>
              <Select value={ncoletor} onValueChange={setNColetor} disabled={carregando}>
                <SelectTrigger data-testid="acesso-coletor-select" className="bg-white/5 border-brand-ice/15 text-brand-ice">
                  <SelectValue placeholder={carregando ? "Carregando..." : "Selecione o dispositivo"} />
                </SelectTrigger>
                <SelectContent>
                  {coletores.map((c) => (
                    <SelectItem key={c.id || c.NColetor} value={String(c.NColetor)}>
                      #{c.NColetor} — {c.DescColetor}{Number(c.tipo_acesso) === 1 ? " [Pedestre]" : " [Veicular]"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {coletorSel ? (
                <p className={`text-[11px] ${ehPedestre ? "text-amber-300" : "text-green-300"}`}>
                  Tipo de Acesso: <strong>{ehPedestre ? "Pedestre" : "Veicular"}</strong>
                  {ehPedestre && " — a placa não será registrada"}
                </p>
              ) : (
                <p className="text-[11px] text-brand-ice/40">Selecione o dispositivo onde o acesso foi efetuado.</p>
              )}
            </div>

            {/* Condutor / Pedestre (segundo, label dinâmico) */}
            <div className="space-y-1">
              <Label className="text-xs text-brand-ice/70 flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                {ehPedestre ? "Pedestre" : "Condutor"}
              </Label>
              <Select value={condutorId} onValueChange={setCondutorId} disabled={carregando}>
                <SelectTrigger data-testid="acesso-condutor-select" className="bg-white/5 border-brand-ice/15 text-brand-ice">
                  <SelectValue placeholder={carregando ? "Carregando..." : `Selecione o ${ehPedestre ? "pedestre" : "condutor"}`} />
                </SelectTrigger>
                <SelectContent>
                  {moradoresEQL.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.nome} — Mat. {m.matricula} {m.tipo_cartao_descricao ? `(${m.tipo_cartao_descricao})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-brand-ice/40">Lista todos os moradores/funcionários ativos da mesma EQL.</p>
            </div>

            {/* v3.13.1/2/7 (Fase A Parte 2) — Botões de acionamento IoT vinculados
                à portaria atual. Cada botão abre um popup ao BSS-Edge local (ZKTeco)
                ou enfileira na Cloud (ESP32). Se o endpoint tem `registra_sentido`
                E/S, ao acionar OK também registra automaticamente. Sufixo -E/-S/-N
                no caption e title contextual. */}
            {iotEndpoints.length > 0 && (
              <div className="space-y-1 pt-1" data-testid="acesso-iot-endpoints">
                <Label className="text-xs text-brand-ice/70 flex items-center gap-1">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  Acionamentos rápidos
                </Label>
                <div className="flex flex-wrap gap-1.5">
                  {iotEndpoints.map((ep) => {
                    const firing = iotFiring === ep.endpoint_id;
                    const sent = (ep.registra_sentido || "").toUpperCase();
                    const sufixo = sent === "E" ? "-E" : (sent === "S" ? "-S" : "-N");
                    const titleTxt =
                      sent === "E" ? `Aciona relés e Registra ENTRADA · Dispositivo #${ep.dispositivo_ncoletor} · Relés ${(ep.reles || []).join(",")}` :
                      sent === "S" ? `Aciona relés e Registra SAÍDA · Dispositivo #${ep.dispositivo_ncoletor} · Relés ${(ep.reles || []).join(",")}` :
                      `Aciona relés (não gera evento automático) · Dispositivo #${ep.dispositivo_ncoletor} · Relés ${(ep.reles || []).join(",")}`;
                    // Cor do sufixo pra reforçar semântica
                    const colorClass =
                      sent === "E" ? "bg-emerald-500/15 hover:bg-emerald-500/25 border-emerald-400/40 text-emerald-100" :
                      sent === "S" ? "bg-rose-500/15 hover:bg-rose-500/25 border-rose-400/40 text-rose-100" :
                      "bg-amber-500/15 hover:bg-amber-500/25 border-amber-400/40 text-amber-100";
                    return (
                      <button
                        key={ep.endpoint_id}
                        type="button"
                        data-testid={`acesso-iot-btn-${ep.endpoint_id}`}
                        disabled={!!iotFiring}
                        onClick={() => dispararEndpoint(ep)}
                        title={titleTxt}
                        className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-heading font-bold transition-colors border ${
                          firing
                            ? "bg-amber-500/40 border-amber-300 text-amber-50 animate-pulse"
                            : colorClass
                        } disabled:opacity-50`}
                      >
                        {ep.endpoint_nome || `Acionar #${ep.dispositivo_ncoletor}`} {sufixo}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-brand-ice/40">
                  Sufixo <code>-E</code>=registra Entrada · <code>-S</code>=registra Saída · <code>-N</code>=só aciona relé (porteiro clica Entrada/Saída depois).
                </p>
              </div>
            )}

            {/* Botões Cancelar / Entrada / Saída */}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                data-testid="acesso-btn-cancelar"
                disabled={salvando}
                onClick={() => onOpenChange(false)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 border border-brand-ice/20 text-brand-ice/80 hover:text-brand-ice font-heading font-bold text-sm transition-colors"
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
              <button
                type="button"
                data-testid="acesso-btn-entrada"
                disabled={salvando || carregando}
                onClick={() => tentarRegistrar(1)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 disabled:opacity-50 border border-green-400/40 text-green-200 font-heading font-bold text-sm transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Entrada
              </button>
              <button
                type="button"
                data-testid="acesso-btn-saida"
                disabled={salvando || carregando}
                onClick={() => tentarRegistrar(2)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 disabled:opacity-50 border border-red-400/40 text-red-200 font-heading font-bold text-sm transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Saída
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AlertDialog — Confirmar quando dispositivo é Pedestre mas existe placa */}
      <AlertDialog open={confirmPedestreOpen} onOpenChange={setConfirmPedestreOpen}>
        <AlertDialogContent className="bg-brand-dark/95 border-amber-500/40 text-brand-ice">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-300 font-heading">Dispositivo de Pedestre</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-ice/80">
              O dispositivo selecionado é de <strong>Pedestre</strong>. A placa <strong className="font-mono">{placaParaMostrar}</strong> <strong>não será registrada</strong> neste acesso. Deseja confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="acesso-pedestre-cancel" className="bg-white/5 border-brand-ice/20 text-brand-ice hover:bg-white/10">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="acesso-pedestre-confirm"
              onClick={() => { setConfirmPedestreOpen(false); executarRegistro(pendingStatus); }}
              className="bg-amber-500/30 border border-amber-400/40 text-amber-100 hover:bg-amber-500/40"
            >
              Sim, registrar mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
